/**
 * Draws a small outline around each parent-child group in the tree, and a dotted
 * outline around clusters of equipment that have no child equipment.
 *
 * Leaf siblings may share one dotted box (up to EQUIPMENT_PER_ROW), except when any sibling
 * under the same parent has children — then each string inverter / conversion leaf gets
 * its own box so borders do not lump inverters together once one subtree expands.
 */

import { useMemo } from 'react';
import type { LayoutNode, VisualEquipment } from '../types';
import { normalizeEquipmentId } from '../idUtils';
import { isConversionAsset } from '../assetTypeConfig';
import { MIN_NODE_GAP } from '../constants';
import { EQUIPMENT_PER_ROW } from '../treeLayout';
import { buildChildrenByParentId } from '../utils/tree-helpers';

/** Breathing room around cards so dashed frames do not hug nodes or collide with nested groups */
const GROUP_BORDER_PADDING = Math.max(10, Math.round(MIN_NODE_GAP * 0.5));
const GROUP_BORDER_STROKE = '#94a3b8';
const GROUP_BORDER_STROKE_WIDTH = 1;
/** Subtle fill for array blocks - mimics solar site substation/block grouping */
const GROUP_FILL = 'rgba(248, 250, 252, 0.45)';

/** Leaf clusters at root only (see filter below); padding matches parent groups */
const LEAF_CLUSTER_PADDING = GROUP_BORDER_PADDING;
const LEAF_BORDER_STROKE = '#94a3b8';
const LEAF_BORDER_STROKE_WIDTH = 1;
/** Dotted pattern (distinct from parent strokeDasharray "6 3") */
const LEAF_STROKE_DASH = '3 5';
const LEAF_FILL = 'rgba(255, 255, 255, 0.25)';
export interface TreeGroupBordersProps {
  layoutNodes: LayoutNode[];
  expandedNodes: Set<string>;
  /** Used to detect leaf equipment the same way as node rendering (parent id match). */
  visualEquipmentList: VisualEquipment[];
}

function parentKeyOf(eq: VisualEquipment): string {
  return normalizeEquipmentId(eq.parentEquipmentId) ?? '__root__';
}

/** True if any equipment under this parent (same sibling set) has at least one child */
function anySiblingUnderParentHasChildren(
  parentKey: string,
  childrenByParentId: Map<string, VisualEquipment[]>,
  rootSiblings: VisualEquipment[]
): boolean {
  const siblings =
    parentKey === '__root__'
      ? rootSiblings
      : (childrenByParentId.get(parentKey) ?? []);
  return siblings.some(
    sib =>
      !sib.isGhost && (childrenByParentId.get(sib.id)?.length ?? 0) > 0
  );
}

function isConversionEquipmentNode(eq: VisualEquipment): boolean {
  if (eq.assetCategory === 'conversion') return true;
  return isConversionAsset(eq.assetType);
}

function getNodeBounds(node: LayoutNode): { left: number; top: number; right: number; bottom: number } {
  return {
    left: node.x - node.width / 2,
    top: node.y,
    right: node.x + node.width / 2,
    bottom: node.y + node.height
  };
}

export default function TreeGroupBorders({
  layoutNodes,
  expandedNodes,
  visualEquipmentList
}: TreeGroupBordersProps) {
  const nodeById = new Map(layoutNodes.map(n => [n.equipment.id, n]));
  const childrenByParentId = useMemo(
    () => buildChildrenByParentId(visualEquipmentList, []),
    [visualEquipmentList]
  );
  const rootSiblings = useMemo(
    () => visualEquipmentList.filter(e => !normalizeEquipmentId(e.parentEquipmentId) && !e.isGhost),
    [visualEquipmentList]
  );

  const hasDirectChildEquipment = (parentId: string) =>
    (childrenByParentId.get(parentId)?.length ?? 0) > 0;

  const groups: Array<{ left: number; top: number; width: number; height: number }> = [];

  /**
   * Dotted boxes for leaf-only equipment (no direct children), max EQUIPMENT_PER_ROW per box.
   * Equipment with children uses only the parent dashed group above, never mixed here.
   */
  const leafClusters: Array<{ left: number; top: number; width: number; height: number }> = [];
  const leafByParent = new Map<string, LayoutNode[]>();
  layoutNodes.forEach(node => {
    const { equipment } = node;
    if (equipment.isGhost) return;
    if (hasDirectChildEquipment(equipment.id)) return;
    const pKey = normalizeEquipmentId(equipment.parentEquipmentId) ?? '__root__';
    // When the parent is expanded, its dashed parent–child frame already bounds these nodes.
    // Extra dotted leaf clusters duplicate borders and cause misaligned “dangling” segments.
    if (pKey !== '__root__' && expandedNodes.has(pKey)) return;
    const list = leafByParent.get(pKey) ?? [];
    list.push(node);
    leafByParent.set(pKey, list);
  });
  const pushLeafClusterBounds = (nodes: LayoutNode[]) => {
    let minL = Infinity;
    let minT = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    nodes.forEach(node => {
      const b = getNodeBounds(node);
      minL = Math.min(minL, b.left);
      minT = Math.min(minT, b.top);
      maxR = Math.max(maxR, b.right);
      maxB = Math.max(maxB, b.bottom);
    });
    leafClusters.push({
      left: minL - LEAF_CLUSTER_PADDING,
      top: minT - LEAF_CLUSTER_PADDING,
      width: maxR - minL + LEAF_CLUSTER_PADDING * 2,
      height: maxB - minT + LEAF_CLUSTER_PADDING * 2
    });
  };
  leafByParent.forEach((nodes, parentKey) => {
    if (nodes.length === 0) return;
    const sorted = [...nodes].sort((a, b) => a.y - b.y || a.x - b.x);
    const splitInverterLeaves = anySiblingUnderParentHasChildren(
      parentKey,
      childrenByParentId,
      rootSiblings
    );

    if (!splitInverterLeaves) {
      for (let i = 0; i < sorted.length; i += EQUIPMENT_PER_ROW) {
        pushLeafClusterBounds(sorted.slice(i, i + EQUIPMENT_PER_ROW));
      }
      return;
    }

    const batch: LayoutNode[] = [];
    const flushBatch = () => {
      if (batch.length === 0) return;
      pushLeafClusterBounds([...batch]);
      batch.length = 0;
    };

    sorted.forEach(node => {
      const conv = isConversionEquipmentNode(node.equipment);
      if (conv) {
        flushBatch();
        pushLeafClusterBounds([node]);
      } else {
        batch.push(node);
        if (batch.length >= EQUIPMENT_PER_ROW) flushBatch();
      }
    });
    flushBatch();
  });

  layoutNodes.forEach(node => {
    const { equipment } = node;
    if (equipment.childEquipmentIds.length === 0) return;
    if (!expandedNodes.has(equipment.id)) return;

    const childNodes = equipment.childEquipmentIds
      .map(id => nodeById.get(id))
      .filter((n): n is LayoutNode => n != null && !n.equipment.isGhost);
    if (childNodes.length === 0) return;

    const bounds = getNodeBounds(node);
    let minL = bounds.left;
    let minT = bounds.top;
    let maxR = bounds.right;
    let maxB = bounds.bottom;

    childNodes.forEach(child => {
      const b = getNodeBounds(child);
      minL = Math.min(minL, b.left);
      minT = Math.min(minT, b.top);
      maxR = Math.max(maxR, b.right);
      maxB = Math.max(maxB, b.bottom);
    });

    groups.push({
      left: minL - GROUP_BORDER_PADDING,
      top: minT - GROUP_BORDER_PADDING,
      width: maxR - minL + GROUP_BORDER_PADDING * 2,
      height: maxB - minT + GROUP_BORDER_PADDING * 2
    });
  });

  return (
    <svg className="absolute top-0 left-0 pointer-events-none overflow-visible" style={{ width: '100%', height: '100%', zIndex: 1 }}>
      {/* Leaf clusters behind parent frames so nested hierarchy reads clearly */}
      {leafClusters.map((g, i) => (
        <rect
          key={`leaf-${i}`}
          x={g.left}
          y={g.top}
          width={g.width}
          height={g.height}
          fill={LEAF_FILL}
          stroke={LEAF_BORDER_STROKE}
          strokeWidth={LEAF_BORDER_STROKE_WIDTH}
          strokeDasharray={LEAF_STROKE_DASH}
          rx={4}
          ry={4}
          strokeLinejoin="round"
        />
      ))}
      {groups.map((g, i) => (
        <rect
          key={`parent-${i}`}
          x={g.left}
          y={g.top}
          width={g.width}
          height={g.height}
          fill={GROUP_FILL}
          stroke={GROUP_BORDER_STROKE}
          strokeWidth={GROUP_BORDER_STROKE_WIDTH}
          strokeDasharray="6 4"
          rx={4}
          ry={4}
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
